import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { styled } from '@mui/system';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import { fromJS } from 'immutable';

import styles from '../../styles';
import ExchangeOrderTitle from '../../../../../components/ExchangeOrderDialog/ExchangeOrderTitle';

import { confirmBookingChangeApi } from '../../../../../apiBooking';
import ExtendedSnackbar from '../../../../../../components/ExtendedSnackbar/ExtendedSnackbar';
import RefreshIndicator from '../../../../../../components/RefreshIndicator/RefreshIndicator';
import { fetchBookingDetails } from '../../../../actionsManageBooking';
import RequestStatusDialog, {
	RequestStatus,
} from '../RequestStatusDialog/RequestStatusDialog';

const StyledDialog = styled(Dialog)(() => ({
	'& .MuiPaper-root': {
		...styles.dialogPaper,
	},
}));

const PreviewDialog = ({
	open,
	handleDialogClose,
	ticketableFareGroups,
	selectedSeats,
	confirmSeatsData,
	refreshBookingOnSuccess,
	refreshBookingParams,
	closeAllDialogs,
	isAmend,
	faresSelected,
	legSolutionId
}) => {
	const [isFetching, setIsFetching] = useState(false);
	const [alertText, setAlertText] = useState('');
	const [requestStatus, setRequestStatus] = useState(null);

	const getSelectedSeatInfo = useCallback((selectedSeats_) => {
		if (!selectedSeats_ || !Object.values(selectedSeats_).length) {
			return null;
		}

		const [segmentId, segmentValue] = Object.entries(selectedSeats_)[0];
		const [passengerId, seatInfo] = Object.entries(segmentValue)[0];
		const { nearbyCoach, nearbySeat, position, coach, ...rest } = seatInfo;

		return {
			formattedSeat:
				nearbyCoach && nearbySeat
					? `Coach ${nearbyCoach} Seat ${nearbySeat}`
					: null,
			nearbyCoach,
			nearbySeat,
			position,
			coach,
			segmentId,
			passengerId,
			...rest,
		};
	}, []);

	const selectedSeatInfo = getSelectedSeatInfo(selectedSeats) || {};

	const handleConfirmSeats = () => {
		setIsFetching(true);
		setRequestStatus(RequestStatus.LOADING);
		setAlertText('');

		const srtChkSeatRes = [];
		const srtNearbyCoach = [];
		const srtNearbySeat = [];
		const srtSeatResOptions = [];
		let priceId = null;
		if (faresSelected.size >= 1) {
			priceId = faresSelected.getIn([0, 'fare', 'priceId']);
		}

		const selectedSeatsFromJs = fromJS(selectedSeats);

		if (!selectedSeatsFromJs.isEmpty()) {
			const travelSegments = selectedSeatsFromJs.keySeq().toList();
			travelSegments.forEach((segment) => {
				const passengers = selectedSeatsFromJs.get(segment).keySeq().toList();
				passengers.forEach((passenger) => {
					if (passenger !== 'enableAllSeats' && passenger !== 'price') {
						const items = selectedSeatsFromJs
							.getIn([segment, passenger])
							.keySeq()
							.toList();
						items.forEach((item) => {
							if (item !== 'price') {
								if (
									item === 'direction' ||
									item === 'position' ||
									item === 'coach' ||
									item === 'enablePassengerSeats' ||
									item === 'nearbyCoach' ||
									item === 'nearbySeat'
								) {
									const value = selectedSeatsFromJs.getIn([
										segment,
										passenger,
										item,
									]);
									if (value) {
										const option = `${value}:${passenger}:${segment}`;
										if (item === 'enablePassengerSeats') {
											srtChkSeatRes.push(option);
										} else if (item === 'nearbyCoach') {
											srtNearbyCoach.push(option);
										} else if (item === 'nearbySeat') {
											srtNearbySeat.push(option);
										} else {
											srtSeatResOptions.push(option);
										}
									}
								} else {
									const option = `${item}:${passenger}:${segment}`;
									srtSeatResOptions.push(option);
								}
							}
						});
					}
				});
			});
		}

		confirmBookingChangeApi(
			{
				dataStorageId: confirmSeatsData.DataStorageId,
				shoppingContext: confirmSeatsData.shoppingContext,
				orderLocator: confirmSeatsData.orderLocator,
				recordLocator: confirmSeatsData.recordLocator,
				ticketableFareLocators: confirmSeatsData.ticketableFareLocators,
				srtChkSeatRes,
				srtNearbyCoach,
				srtNearbySeat,
				srtSeatResOptions: srtSeatResOptions.length ? srtSeatResOptions : [],
				srtPriceId: priceId,
				legSolutionId
			},
			(error) => {
				const errorMsg = error?.errorResponse?.message;
				setAlertText(errorMsg);
				setIsFetching(false);
				setRequestStatus(RequestStatus.FAILURE);
			},
			() => {
				setIsFetching(false);
				setAlertText('Successfully updated seats');
				setRequestStatus(RequestStatus.SUCCESS);

				setTimeout(() => {
					refreshBookingOnSuccess(refreshBookingParams);
					closeAllDialogs();
					window.location.reload();
				}, 1500);
			},
		);
	};

	return (
		<StyledDialog
			open={open}
			onClose={handleDialogClose}
			disableEnforceFocus
			maxWidth={false}
		>
			<DialogTitle>Seat Amendments Summary</DialogTitle>
			<DialogContent>
				<div>
					<h2>Previous Reservation</h2>
					<ExchangeOrderTitle
						exchangeableTicketableFareIds={
							ticketableFareGroups[0].BookingDetailsLegTicketableFareIds
						}
						isSeasonTrip={false}
						ticketableFareGroups={ticketableFareGroups}
						showSeatsAndServices
					/>
				</div>
				<br />
				<div>
					<h2>Amended Reservation</h2>
					<ExchangeOrderTitle
						exchangeableTicketableFareIds={
							ticketableFareGroups[0].BookingDetailsLegTicketableFareIds
						}
						isSeasonTrip={false}
						ticketableFareGroups={ticketableFareGroups}
						showSeatsAndServices
						selectedSeat={selectedSeatInfo.formattedSeat}
						fareSelected={faresSelected}
					/>
				</div>
			</DialogContent>
			<DialogActions>
				<div style={styles.buttonActions}>
					<Button variant="contained" onClick={handleDialogClose}>
						Change Seats
					</Button>
					<Button
						variant="contained"
						color="primary"
						style={styles.confirmButton}
						onClick={handleConfirmSeats}
					>
						Confirm
					</Button>
				</div>
			</DialogActions>
			<ExtendedSnackbar
				id="srtAmendSeatReservationsPreviewSnackBar"
				open={alertText !== ''}
				message={alertText}
				onClose={() => setAlertText('')}
			/>
			{isAmend && requestStatus && (
				<RequestStatusDialog
					open={!!requestStatus}
					status={requestStatus}
					failureMessage={alertText}
					onClose={() => setRequestStatus(null)}
				/>
			)}

			{!isAmend && (
				<div className="container-fluid">
					<RefreshIndicator
						size={36}
						top={0}
						left={0}
						status={isFetching ? 'loading' : 'hide'}
						style={{
							marginTop: '10%',
							marginLeft: '45%',
						}}
					/>
				</div>
			)}
		</StyledDialog>
	);
};

PreviewDialog.propTypes = {
	open: PropTypes.bool,
	handleDialogClose: PropTypes.func,
	ticketableFareGroups: PropTypes.array,
	selectedSeats: PropTypes.any,
	confirmSeatsData: PropTypes.object,
	refreshBookingOnSuccess: PropTypes.func,
	refreshBookingParams: PropTypes.any,
	closeAllDialogs: PropTypes.func,
	isAmend: PropTypes.bool,
	faresSelected: PropTypes.array,
	legSolutionId: PropTypes.number
};

const mapDispatchToProps = (dispatch) => ({
	refreshBookingOnSuccess: bindActionCreators(fetchBookingDetails, dispatch),
});

export default connect(null, mapDispatchToProps)(PreviewDialog);
